

import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SidebarItem } from "./sidebar-item";
import { AlertTriangle } from "lucide-react";

interface GuidelinesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const GuidelinesDialog: React.FC<GuidelinesDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const [accepted, setAccepted] = React.useState(() => {
    const savedAcceptance = localStorage.getItem('guidelines-accepted');
    return savedAcceptance === 'true';
  });

  const handleAcceptanceChange = (checked: boolean) => {
    setAccepted(checked);
    localStorage.setItem('guidelines-accepted', checked.toString());
    
    // Dispatch a custom event to notify other components
    window.dispatchEvent(new Event('guidelines-acceptance-changed'));
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <SidebarItem
          href="#"
          icon={<AlertTriangle className="h-5 w-5" />}
          title="Retningslinjer"
          isActive={false}
          onClick={() => onOpenChange(true)}
        />
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto z-[100]">
        <DialogHeader>
          <DialogTitle>Retningslinjer for bruk av Klara</DialogTitle>
          <DialogDescription>
            Vennligst les gjennom retningslinjene for bruk av Klara.
          </DialogDescription>
        </DialogHeader>
        <div className="prose prose-sm mt-4">
          <section className="space-y-4">
            <div>
              <h3 className="font-bold text-lg">1. Generell bruk og ansvar</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Klara er et beslutningsstøtteverktøy for jobbveiledere. Den skal ikke brukes direkte av tiltaksdeltakere.</li>
                <li>Klara gir generell innsikt i arbeidsmarkedet og skal ikke brukes til vurdering av enkeltpersoner.</li>
                <li>Jobbveiledere har ansvar for å bruke Klara i tråd med disse retningslinjene og tiltaksarrangørens øvrige føringer.</li>
                <li>Alle brukere av Klara skal ha gjennomgått opplæring i bruken for å sikre trygg og korrekt bruk.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg">2. Klara er en språkmodell – Kritisk evaluering er nødvendig</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Klara er bygget på en stor språkmodell (KI) som analyserer og genererer tekst basert på enorme mengder tidligere informasjon.
                  <ul className="list-disc pl-6 mt-2">
                    <li>Den forstår ikke informasjonen den gir, men forutsier sannsynlige svar basert på mønstre.</li>
                    <li>Den kan produsere feilaktige eller unøyaktige svar, og det er derfor viktig å vurdere informasjonen kritisk.</li>
                  </ul>
                </li>
                <li>Klara er et hjelpemiddel for jobbveiledere, ikke en fasit.
                  <ul className="list-disc pl-6 mt-2">
                    <li>Alle svar må tolkes med skjønn og vurderes opp mot annen relevant kunnskap.</li>
                    <li>Hvis et svar virker feil eller usannsynlig, bør det sjekkes opp mot andre kilder før det brukes.</li>
                  </ul>
                </li>
                <li>KI-baserte systemer har innebygde svakheter som bias og feilfortolkninger. Dette betyr at svarene kan ha skjevheter basert på hvilke data modellen er trent på.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg">3. Personvern – Ingen personopplysninger</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Det er ikke tillatt å legge inn personopplysninger i Klara. Dette inkluderer navn, kontaktinformasjon, fødselsdato, helseopplysninger eller andre identifiserbare opplysninger.</li>
                <li>Informasjon som kan knyttes til enkeltpersoner, som «jurist, 43 år, fra Bodø», må også unngås.</li>
                <li>Klara skal kun brukes til generelle spørsmål om arbeidsmarkedet, som f.eks. «Hvilke bransjer har størst behov for arbeidskraft de neste fem årene?»</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg">4. Begrensninger i bruken</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Klara skal kun brukes til å hente informasjon om arbeidsmarkedet, utdanninger og generelle jobbmuligheter.</li>
                <li>Den skal ikke brukes til å avgjøre eller anbefale tiltak for en enkelt deltaker.</li>
                <li>Klara gir ikke alltid korrekte svar og må brukes med kritisk vurdering.</li>
                <li>Klara skal ikke brukes til direkte bedriftskontakt i kommuner med færre enn 1000 innbyggere, da det kan føre til identifiserbare formidlinger på grunn av få bedrifter.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg">5. Ansvarlig og etisk bruk</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Klara er et hjelpemiddel, ikke en erstatning for jobbveilederens kompetanse og erfaring.</li>
                <li>Klara skal ikke brukes på en måte som påvirker tiltaksdeltakerens valg direkte.</li>
                <li>Jobbveiledere skal være bevisste på asymmetri i maktforholdet mellom seg selv og deltakeren, og aldri bruke Klara til å presse noen til en bestemt vei.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg">6. Informasjon til tiltaksdeltakere</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Hvis informasjon fra Klara deles med en deltaker, må det understrekes at dette kun er en veiledende markedsinnsikt.</li>
                <li>Deltakere har rett til å få informasjon om at Klara brukes, men det er ikke nødvendig eller påkrevd at de involveres i bruken.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg">7. Evaluering og oppfølging</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Alle brukere av Klara må ha gjennomført opplæring for å sikre at verktøyet brukes korrekt og ansvarlig.</li>
                <li>Eventuelle feil, upresise svar eller etiske bekymringer knyttet til Klara må rapporteres til tiltaksansvarlig.</li>
                <li>Bruken av Klara bør evalueres jevnlig for å sikre at den brukes riktig og at den gir nyttig informasjon.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg">8. Tilpasning til nye regler og anbefalinger</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Teknologi og regelverk endrer seg. Jobbveiledere skal holde seg oppdatert på eventuelle endringer i bruken av Klara.</li>
                <li>Dersom nye retningslinjer fra NAV eller andre myndigheter krever endringer, må bruken justeres i tråd med dette.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg">9. Lokale tilpasninger</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Disse retningslinjene gjelder for alle som bruker Klara, men kan tilpasses av den enkelte tiltaksarrangør så lenge hovedprinsippene følges.</li>
              </ul>
            </div>
          </section>
        </div>
        <div className="flex items-center space-x-2 mt-6 border-t pt-4">
          <Checkbox
            id="guidelines-acceptance"
            checked={accepted}
            onCheckedChange={handleAcceptanceChange}
          />
          <Label htmlFor="guidelines-acceptance" className="text-sm">
            Jeg har lest, forstått og forplikter meg til å overholde disse retningslinjene
          </Label>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={handleClose} variant="outline">
            Lukk
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

